import React from 'react';

import { useFormContext } from 'react-hook-form';

import { ETemplateStudioSidebarBlock } from 'state/template-studio.state.service';
import { z } from 'zod';

import AcGradientInput from 'components/AcGradientInput/AcGradientInput';
import Input from 'components/Input/Input';
import Switch from 'components/Switch/Switch';
import { FormControl, FormField, FormItem, FormMessage } from 'components/ui';
import { Separator } from 'components/ui/separator';

import { createColorSetValueHandler } from '../../../utils/blockForms.utils';
import { templateStudioSchema } from '../../../validation-schema';
import { DesignBlockMenuItem } from '../Components/DesignBlockMenuItem';

import './style.css';

export const TemplateStudioHeaderForm: React.FC = () => {
  const {
    control,
    setValue,
    watch,
    formState: { errors }
  } = useFormContext<z.infer<typeof templateStudioSchema>>();

  const colorSetValueHandler = (onChange: (value: any) => void) =>
    createColorSetValueHandler(
      'header.textColor',
      () => watch('header.textColor'),
      setValue as (name: string, value: any) => void,
      onChange
    );

  const hasTextColorError = Boolean(errors.header?.textColor);
  const textColorHelperText = errors.header?.textColor?.toString() || '';
  const opacityError = errors.header?.backgroundOpacity?.message;

  return (
    <DesignBlockMenuItem
      blockType={ETemplateStudioSidebarBlock.HEADER}
      className="design-block-menu-item text-sm"
    >
      <Separator className="bg-base-border w-[288px] ml-[-2px]" />

      <FormField
        control={control}
        name="header.showLogo"
        render={({ field }) => (
          <Switch
            key={field.value ? 'on' : 'off'}
            label="Show Logo"
            className="h-[27px] align-middle"
            textAlignment="left"
            defaultChecked={field.value}
            checked={field.value}
            onCheckedChange={field.onChange}
            data-testid="TemplatesStudio-Header-showLogo"
          />
        )}
      />
      <FormField
        control={control}
        name="header.showPlayerName"
        render={({ field }) => (
          <Switch
            key={field.value ? 'on' : 'off'}
            label="Show Player Name"
            className="h-[27px] align-middle"
            textAlignment="left"
            defaultChecked={field.value}
            checked={field.value}
            onCheckedChange={field.onChange}
            data-testid="TemplatesStudio-Header-showPlayerName"
          />
        )}
      />
      <FormField
        control={control}
        name="header.showPlayerId"
        render={({ field }) => (
          <Switch
            key={field.value ? 'on' : 'off'}
            label="Show Player ID"
            className="h-[27px] align-middle"
            textAlignment="left"
            defaultChecked={field.value}
            checked={field.value}
            onCheckedChange={field.onChange}
            data-testid="TemplatesStudio-Header-showPlayerId"
          />
        )}
      />
      <FormField
        control={control}
        name="header.isSticky"
        render={({ field }) => (
          <Switch
            key={field.value ? 'on' : 'off'}
            label="Sticky Header"
            className="h-[27px] align-middle"
            textAlignment="left"
            defaultChecked={field.value}
            checked={field.value}
            onCheckedChange={field.onChange}
            data-testid="TemplatesStudio-Header-sticky"
          />
        )}
      />
      <FormField
        control={control}
        name="header.backgroundOpacity"
        render={({ field: { onChange, value } }) => (
          <FormItem data-field="header-backgroundOpacity">
            <FormControl>
              <Input
                label="Background Opacity (%)"
                name="header.backgroundOpacity"
                type="number"
                placeholder="0"
                className="flex-row items-center justify-between"
                inputClassName={`w-[95px] h-[30px] ml-5 ${opacityError ? 'border-base-destructive' : ''}`}
                labelClassName="mb-0"
                value={value}
                min={0}
                max={100}
                onChange={(e) => onChange(Number(e.target.value))}
                data-testid="TemplatesStudio-Header-backgroundOpacity"
                state={opacityError ? 'error' : 'default'}
                description={opacityError}
              />
            </FormControl>
            {!opacityError && <FormMessage />}
          </FormItem>
        )}
      />
      <FormField
        name="header.textColor"
        control={control}
        render={({ field: { value, onChange } }) => (
          <FormItem data-field="header-textColor">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Text color</label>
              <FormControl>
                <div className="[&_.MuiInputAdornment-root]:mr-0 [&_.MuiTextField-root]:!w-[95px] [&_.MuiInputBase-input]:!px-3 [&_.MuiInputBase-input]:!py-2 [&_.MuiInputBase-input]:!pl-0">
                  <AcGradientInput
                    name="header.textColor"
                    defaultValue={value || { colorOne: '#FFF' }}
                    setValue={colorSetValueHandler(onChange)}
                    onlySolid={true}
                    circular={true}
                    imgWidth="16px"
                    imgHeight="16px"
                    error={hasTextColorError}
                    helperText={textColorHelperText}
                    data-testid="TemplatesStudio-Header-textColor"
                  />
                </div>
              </FormControl>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    </DesignBlockMenuItem>
  );
};
